import { useMemo } from 'react'
import { ChertoExperiments } from 'constants/experiments'
import { FlightsClassTypes } from 'constants/flight'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useIsDesktopWithSsrFallback } from 'hooks/useIsDesktopWithSsrFallback'
import { isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { getFlashCancellationPolicyView } from 'components/Common/CancellationPolicies/Flash/getFlashCancellationPolicyView'
import { getOfferCancellationPolicySelection } from 'components/Common/CancellationPolicies/Flash/getOfferCancellationPolicySelection'
import { resolveOfferFlashCancellationTimelineSurface } from './resolveOfferFlashCancellationTimelineSurface'

const READ_EXPERIMENT_WITHOUT_ACTIVATION = { canActivate: false } as const

interface UseOfferFlashCancellationTimelineSurfaceParams {
  offer: App.HotelOffer | App.Offer
  checkIn?: string
  rooms: Array<App.Occupants>
  fareClass?: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  /**
   * Must be explicitly true to mount flexible timeline context.
   * Undefined/false keeps the surface hidden (avoids optimistic mount).
   */
  availableForPurchase?: boolean
  cheapestSelectedOption?: App.OfferPackageOptionView
}

/**
 * Shared offer-page cancellation timeline surface: policy view, desktop gate, and USP dedupe.
 * Experiment activation stays in OfferCancellationPolicyTimelineExperiment.
 */
export function useOfferFlashCancellationTimelineSurface(
  params: UseOfferFlashCancellationTimelineSurfaceParams,
) {
  const {
    offer,
    checkIn,
    rooms,
    fareClass = FlightsClassTypes.ECONOMY,
    charterBundleRoute,
    availableForPurchase,
    cheapestSelectedOption,
  } = params

  const isDesktop = useIsDesktopWithSsrFallback()
  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)

  const { cancellationPolicy, charterNights } = getOfferCancellationPolicySelection(
    offer,
    cheapestSelectedOption,
  )

  // Only resolve departure when it can affect this surface — avoids a charter flight-search
  // fetch for sold-out offers, missing check-in, or non-charter policies (FlashOfferSummary /
  // RateOption may still fetch independently when those UI paths mount). Without check-in,
  // useCharterFlightDates cannot resolve a departure, but passing the route still enables
  // useAllCharterFlightJourneys / fetchFlights; the generic no-date timeline does not use it.
  const needsCharterDepartDate = (
    availableForPurchase === true &&
    !!checkIn &&
    !!charterBundleRoute &&
    isCharterModeEnabled &&
    isCharterCancellationPolicy(cancellationPolicy)
  )

  const { departDate: charterDepartDateFromFlights } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: charterNights,
    charterBundleRoute: needsCharterDepartDate ? charterBundleRoute : undefined,
    rooms,
    fareClass,
  })

  const charterDepartDate = needsCharterDepartDate ? charterDepartDateFromFlights : undefined

  const policyView = useMemo(() => getFlashCancellationPolicyView({
    cancellationPolicy,
    propertyTimezoneOffset: offer.property?.timezoneOffset,
    checkInDate: checkIn,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    // Opt-in only — do not treat undefined as purchasable.
    availableForPurchase: availableForPurchase === true,
  }), [
    availableForPurchase,
    cancellationPolicy,
    charterDepartDate,
    checkIn,
    isCharterModeEnabled,
    offer.property?.timezoneOffset,
    purchaseDate,
  ])

  const { isAnyVariant: isCancellationTimelineVariant } = useExperiment(
    ChertoExperiments.croCancellationTimelineOnOfferPage,
    READ_EXPERIMENT_WITHOUT_ACTIVATION,
  )

  const surface = resolveOfferFlashCancellationTimelineSurface({
    isDesktop,
    isCancellationTimelineVariant,
    hasFlexibleTimelineContext: !!policyView.flexibleTimelineContext,
  })

  return {
    ...surface,
    policyView,
    timelineContext: policyView.flexibleTimelineContext,
  }
}
